import type { TimelineEntry } from "@freightsimple/observability/observability";

// Distilled view of the cold-load TimelineEntry ring buffer. Counts auth
// silent-token outcomes, captures key phase durations, and exposes the
// boot.total / first_render milestones used by both the engineering panel
// and the user-facing ColdStartFriendlyNotice.

export interface Summary {
  silentAttempts: number;
  silentSuccess: number;
  silentRedirected: number;
  silentShortCircuit: number;
  silentRedirectFailed: number;
  redirectInitiated: number;
  redirectInProgressThrown: number;
  authInitMs: number | undefined;
  msalInitializeMs: number | undefined;
  bootTotalMs: number | undefined;
  firstRenderMs: number | undefined;
}

export function fmtMs(ms: number | undefined): string {
  if (ms === undefined) return "—";
  if (ms < 1000) return `${ms.toFixed(0)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

export function fmtAttrs(
  attrs: Record<string, string | number | boolean>,
): string {
  return Object.entries(attrs)
    .map(([k, v]) => `${k}=${String(v)}`)
    .join(" ");
}

// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
export function summarizeColdStart(entries: readonly TimelineEntry[]): Summary {
  const summary: Summary = {
    silentAttempts: 0,
    silentSuccess: 0,
    silentRedirected: 0,
    silentShortCircuit: 0,
    silentRedirectFailed: 0,
    redirectInitiated: 0,
    redirectInProgressThrown: 0,
    authInitMs: undefined,
    msalInitializeMs: undefined,
    bootTotalMs: undefined,
    firstRenderMs: undefined,
  };
  const startsByName = new Map<string, number>();
  for (const e of entries) {
    if (e.name === "auth.silent_token.start") summary.silentAttempts++;
    if (e.name === "auth.silent_token.end") {
      const result = e.attrs?.["result"];
      if (result === "success") summary.silentSuccess++;
      if (result === "redirected") summary.silentRedirected++;
      if (result === "short_circuit") summary.silentShortCircuit++;
      if (result === "redirect_failed") summary.silentRedirectFailed++;
    }
    if (e.name === "auth.redirect_initiated") summary.redirectInitiated++;
    if (e.name === "auth.redirect_in_progress_thrown")
      summary.redirectInProgressThrown++;
    if (e.name === "boot.first_render") summary.firstRenderMs = e.t;

    if (e.name.endsWith(".start")) {
      const base = e.name.slice(0, -".start".length);
      startsByName.set(base, e.t);
    }
    if (e.name.endsWith(".end")) {
      const base = e.name.slice(0, -".end".length);
      const startT = startsByName.get(base);
      if (startT !== undefined) {
        const dur = e.t - startT;
        if (base === "auth.init") summary.authInitMs = dur;
        if (base === "auth.msal_initialize") summary.msalInitializeMs = dur;
        if (base === "boot.total") summary.bootTotalMs = dur;
      }
    }
  }
  return summary;
}
